/**
 * Settings › Notifications: the person's Lykke e-mails (cowork /v1/lykke/settings). Urgent items (a payment to
 * release, an answer from Beatly, anything due within two days) right away; everything else in one e-mail a day.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { lykkeApi } from './lykkeApi';

function Box({ on, label, hint, disabled, onToggle }: { on: boolean; label: string; hint: string; disabled?: boolean; onToggle: () => void }) {
  return (
    <label className="group flex cursor-pointer items-start gap-3">
      <button type="button" role="checkbox" aria-checked={on} onClick={onToggle} disabled={disabled}
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all ${on ? 'border-[#be43ff] bg-[#be43ff]' : 'border-gray-300 bg-white group-hover:border-gray-400'}`}>
        {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </button>
      <span className="flex flex-col">
        <span className="text-sm text-[#344054]">{label}</span>
        <span className="text-xs text-[#667085]">{hint}</span>
      </span>
    </label>
  );
}

export function LykkeEmailSettings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['lykke', 'settings'], queryFn: () => lykkeApi.settings(), retry: 1, staleTime: 60_000 });
  const m = useMutation({
    mutationFn: (s: { urgent?: boolean; digest?: boolean }) => lykkeApi.saveSettings(s),
    onSuccess: (d) => qc.setQueryData(['lykke', 'settings'], d),
  });
  if (!q.data) return null;
  const s = m.isPending && m.variables ? { ...q.data, ...m.variables } : q.data;
  return (
    <div className="mt-5 space-y-3 border-t border-gray-100 pt-5" data-lykke-email-settings>
      <div className="text-sm font-semibold text-[#101828]">Lykke e-mails{q.data.email ? <span className="font-normal text-[#667085]"> to {q.data.email}</span> : null}</div>
      <Box on={s.urgent} disabled={m.isPending} onToggle={() => m.mutate({ urgent: !s.urgent })} label="Urgent things right away" hint="A creator payment to release, an answer from Beatly, anything due within two days." />
      <Box on={s.digest} disabled={m.isPending} onToggle={() => m.mutate({ digest: !s.digest })} label="One daily e-mail with the rest" hint="Every morning: what needs you, by campaign." />
    </div>
  );
}
